import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Save, ArrowUp, ArrowDown, Plus, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

const GRAINS = [
  { key: "entry", label: "Entry (one row per punch)" },
  { key: "daily", label: "Daily (one row per worker/day)" },
  { key: "weekly", label: "Weekly (one row per worker/Sat-Fri)" },
];
const FILETYPES = [
  { key: "csv", label: "CSV (UTF-8)" },
  { key: "csv_bom", label: "CSV (UTF-8-BOM, Excel-safe)" },
  { key: "xlsx", label: "XLSX (Excel workbook)" },
];
const STATUS_OPTS = ["draft", "submitted", "approved", "rejected"];
const SOURCE_OPTS = ["web", "mobile", "kiosk", "admin", "pto", "api",
                     "geofence", "task_start", "ai_generated", "import"];

export default function ExportProfileEditPage() {
  const { profileId } = useParams();
  const isNew = !profileId;
  const navigate = useNavigate();

  const [library, setLibrary] = useState([]);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [form, setForm] = useState({
    name: "", description: "",
    filters: {
      from: firstOfMonth(), to: today(),
      statuses: [], worksite_codes: [], identity_emails: [],
      sources: [],
    },
    columns: [],
    grain: "entry", filetype: "csv",
  });

  useEffect(() => {
    api.get("/tenant/exports/columns").then(({ data }) => setLibrary(data.columns || []));
    if (!isNew) {
      api.get(`/tenant/export-profiles/${profileId}`).then(({ data }) => {
        setForm({
          name: data.name || "", description: data.description || "",
          filters: { from: today(), to: today(), statuses: [], worksite_codes: [], identity_emails: [], sources: [], ...data.filters },
          columns: data.columns || [],
          grain: data.grain || "entry", filetype: data.filetype || "csv",
        });
      });
    }
  }, [profileId, isNew]);

  const availableColumns = useMemo(() => {
    const selectedKeys = new Set(form.columns.map((c) => c.key));
    return library
      .filter((c) => !selectedKeys.has(c.key))
      .filter((c) => grainOf(c).includes(form.grain));
  }, [library, form.columns, form.grain]);

  // Selected columns that don't support the current grain — surfaced
  // inline so the operator sees what will be dropped at run time.
  const incompatibleSelected = useMemo(() => {
    return form.columns.filter((c) => {
      const supported = grainOf(library.find((l) => l.key === c.key));
      return supported.length > 0 && !supported.includes(form.grain);
    });
  }, [library, form.columns, form.grain]);

  const move = (idx, delta) => {
    const cols = [...form.columns];
    const target = idx + delta;
    if (target < 0 || target >= cols.length) return;
    [cols[idx], cols[target]] = [cols[target], cols[idx]];
    setForm({ ...form, columns: cols });
  };

  const addColumn = (key) => {
    const c = library.find((l) => l.key === key);
    if (!c) return;
    setForm({ ...form, columns: [...form.columns, { key, label: c.label, format: c.number_format || undefined }] });
  };

  const removeColumn = (idx) => {
    const cols = [...form.columns]; cols.splice(idx, 1);
    setForm({ ...form, columns: cols });
  };

  const relabel = (idx, label) => {
    const cols = [...form.columns]; cols[idx] = { ...cols[idx], label };
    setForm({ ...form, columns: cols });
  };

  const runPreview = async () => {
    if (form.columns.length === 0) return toast.error("Add at least one column");
    setPreviewing(true);
    try {
      const { data } = await api.post("/tenant/exports/preview", {
        filters: form.filters, columns: form.columns.map((c) => ({ key: c.key, label: c.label, format: c.format })),
        grain: form.grain, filetype: form.filetype,
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPreviewing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name, description: form.description,
        filters: form.filters,
        columns: form.columns.map((c) => ({ key: c.key, label: c.label, format: c.format })),
        grain: form.grain, filetype: form.filetype,
      };
      const res = isNew
        ? await api.post("/tenant/export-profiles", body)
        : await api.put(`/tenant/export-profiles/${profileId}`, body);
      toast.success(isNew ? "Profile created" : "Profile saved");
      navigate(`/app/admin/exports/profiles/${res.data.id || profileId}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const toggleMulti = (key, value) => {
    const arr = new Set(form.filters[key] || []);
    if (arr.has(value)) arr.delete(value); else arr.add(value);
    setForm({ ...form, filters: { ...form.filters, [key]: [...arr] } });
  };

  return (
    <div className="space-y-6" data-testid="export-profile-edit">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
          <Link to="/app/admin/exports" className="hover:underline">Exports</Link>
          {" / "}<Link to="/app/admin/exports/profiles" className="hover:underline">Profiles</Link>
          {" / "}{isNew ? "New" : "Edit"}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">
          {isNew ? "New export profile" : form.name || "Profile"}
        </h1>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">1. Basics</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="profile-name" placeholder="Weekly ops summary" />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label>Grain</Label>
              <Select value={form.grain} onValueChange={(v) => setForm({ ...form, grain: v })}>
                <SelectTrigger data-testid="profile-grain"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GRAINS.map((g) => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label>Filetype</Label>
              <Select value={form.filetype} onValueChange={(v) => setForm({ ...form, filetype: v })}>
                <SelectTrigger data-testid="profile-filetype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FILETYPES.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">2. Filters</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <div>
              <Label>From</Label>
              <Input type="date" value={form.filters.from}
                     onChange={(e) => setForm({ ...form, filters: { ...form.filters, from: e.target.value } })}
                     data-testid="filter-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={form.filters.to}
                     onChange={(e) => setForm({ ...form, filters: { ...form.filters, to: e.target.value } })}
                     data-testid="filter-to" />
            </div>
          </div>
          <div>
            <Label className="mb-1 block">Timesheet status</Label>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTS.map((s) => (
                <label key={s} className={"flex items-center gap-2 rounded border px-2 py-1 text-sm " + ((form.filters.statuses || []).includes(s) ? "border-primary bg-primary/5" : "border-border")}>
                  <Checkbox checked={(form.filters.statuses || []).includes(s)} onCheckedChange={() => toggleMulti("statuses", s)} />
                  <span className="capitalize">{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1 block">Punch source</Label>
            <div className="flex flex-wrap gap-2">
              {SOURCE_OPTS.map((s) => (
                <label key={s} className={"flex items-center gap-2 rounded border px-2 py-1 text-sm " + ((form.filters.sources || []).includes(s) ? "border-primary bg-primary/5" : "border-border")}>
                  <Checkbox checked={(form.filters.sources || []).includes(s)} onCheckedChange={() => toggleMulti("sources", s)} />
                  <span>{s}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Worksite codes (comma-separated)</Label>
              <Input placeholder="HQ-01, SITE-A"
                     value={(form.filters.worksite_codes || []).join(", ")}
                     onChange={(e) => setForm({ ...form, filters: { ...form.filters, worksite_codes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                     data-testid="filter-worksite-codes" />
            </div>
            <div>
              <Label>Worker emails (comma-separated)</Label>
              <Input placeholder="wanda@acme.test"
                     value={(form.filters.identity_emails || []).join(", ")}
                     onChange={(e) => setForm({ ...form, filters: { ...form.filters, identity_emails: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } })}
                     data-testid="filter-identity-emails" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">3. Columns ({form.columns.length})</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">Available</div>
            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
              {availableColumns.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">
                  All available columns for this grain are already selected.
                </div>
              )}
              {availableColumns.map((c) => (
                <button key={c.key} type="button" onClick={() => addColumn(c.key)}
                        className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-left hover:bg-primary/5"
                        data-testid={`col-add-${c.key}`}>
                  <div>
                    <div className="text-sm font-medium">{c.label}</div>
                    <div className="text-xs text-muted-foreground">{c.category} · {c.key}</div>
                  </div>
                  <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between text-xs font-medium uppercase tracking-widest text-muted-foreground">
              <span>Selected (order = column order in output)</span>
              {incompatibleSelected.length > 0 && (
                <span className="rounded bg-amber-100 px-2 py-0.5 normal-case text-amber-800"
                      data-testid="grain-incompat-notice">
                  {incompatibleSelected.length} column{incompatibleSelected.length === 1 ? "" : "s"} hidden at
                  {" "}{form.grain} grain — switch to {supportedGrainsHint(incompatibleSelected, library)} to include {incompatibleSelected.length === 1 ? "it" : "them"}
                </span>
              )}
            </div>
            <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
              {form.columns.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground">
                  Click a column on the left to add it. Reorder with the arrows.
                </div>
              )}
              {form.columns.map((c, i) => {
                const supported = grainOf(library.find((l) => l.key === c.key));
                const compatible = supported.length === 0 || supported.includes(form.grain);
                return (
                <div key={c.key}
                     className={"flex items-center gap-2 border-b border-border px-3 py-2 " + (compatible ? "" : "opacity-50")}
                     data-testid={`col-selected-${c.key}`}>
                  <div className="flex-1">
                    <Input value={c.label || c.key} onChange={(e) => relabel(i, e.target.value)}
                           className="h-8 text-sm" disabled={!compatible} />
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      {c.key}
                      {!compatible && (
                        <span className="ml-1 text-amber-700"
                              data-testid={`col-incompat-${c.key}`}>
                          · dropped at {form.grain}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}
                            data-testid={`col-move-up-${c.key}`}><ArrowUp className="h-3 w-3" /></Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === form.columns.length - 1}
                            data-testid={`col-move-down-${c.key}`}><ArrowDown className="h-3 w-3" /></Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeColumn(i)}
                            data-testid={`col-remove-${c.key}`}><Trash2 className="h-3 w-3 text-rose-600" /></Button>
                  </div>
                </div>
              );})}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">4. Preview</CardTitle>
          <Button variant="outline" size="sm" onClick={runPreview} disabled={previewing || form.columns.length === 0}
                  data-testid="profile-preview-btn">
            {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
            Preview 10 rows
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          {!preview && <div className="p-6 text-center text-sm text-muted-foreground">
            Preview runs against the current filter set without writing a file.
          </div>}
          {preview && (
            <div>
              <div className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
                Total matched rows for this filter: {preview.total_matched_rows}
              </div>
              <table className="min-w-full text-xs" data-testid="profile-preview-table">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    {form.columns.map((c) => (
                      <th key={c.key} className="whitespace-nowrap px-3 py-2 font-medium">
                        {c.label || c.key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      {form.columns.map((c) => (
                        <td key={c.key} className="whitespace-nowrap px-3 py-2">{String(r[c.key] ?? "")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" asChild><Link to="/app/admin/exports">Cancel</Link></Button>
        <Button onClick={save} disabled={saving || !form.name || form.columns.length === 0}
                data-testid="profile-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isNew ? "Create profile" : "Save"}
        </Button>
      </div>
    </div>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
function firstOfMonth() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

/**
 * Return the supported-grains array of a column library entry.
 * Prefers the new `supported_grains` key (Phase 25b patch); falls back
 * to `grain` for older API responses.
 */
function grainOf(col) {
  if (!col) return [];
  return col.supported_grains || col.grain || [];
}

function supportedGrainsHint(cols, library) {
  const s = new Set();
  cols.forEach((c) => {
    const lib = library.find((l) => l.key === c.key);
    grainOf(lib).forEach((g) => s.add(g));
  });
  return [...s].join(" or ") || "weekly or daily";
}
